import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import type { AlbumDetail, StudioSort, Visibility } from '@photos/shared';
import { useStudioPhoto, useStudioPhotos, type StudioQuery } from '../../api/hooks';
import { Hero } from '../../components/Hero';
import { Icon } from '../../components/Icon';
import { PhotoGrid, type Grouping } from '../../components/PhotoGrid';
import { TagSearch } from '../../components/SearchBox';
import { SelectionBar } from '../../components/SelectionBar';
import { usePhotoGridState } from '../../hooks/usePhotoGridState';
import { fmtCount, fmtRangeIso, fmtTotal } from '../../lib/format';
import { VISIBILITY_LOOK } from '../../lib/visibility';
import { type OrientFilter } from '../../state/libraryParams';
import { useSession } from '../../state/session';
import { ORIENT } from '../ResultsView';
import { StudioActions } from './StudioActions';
import { StudioLightbox } from './StudioLightbox';

const SORTS: { key: StudioSort; label: string }[] = [
  { key: 'taken-desc', label: 'Date taken ↓' },
  { key: 'taken-asc', label: 'Date taken ↑' },
  { key: 'uploaded', label: 'Upload date' },
  { key: 'name', label: 'File name' },
];
const GROUPING: Record<StudioSort, Grouping> = { 'taken-desc': 'taken', 'taken-asc': 'taken', uploaded: 'uploaded', name: 'none' };

interface StudioPhotosViewProps {
  title: string;
  /** the album page; omitted for the cross-album views */
  album?: AlbumDetail;
  /** Unreleased / Restricted views */
  fixedVisibility?: Visibility;
  defaultSort?: StudioSort;
  notice?: string;
  empty: string;
  /** replaces the banner title (inline rename) */
  heading?: ReactNode;
  heroSub?: string[];
  heroActions?: ReactNode;
  /** under the banner (e.g. collaborators) */
  children?: ReactNode;
}

/**
 * A Studio photo grid: banner, toolbar (visibility chips, No tags yet, tag filter, uploader, sort, shape), the same
 * justified day grid as Browse with visibility badges, the Studio selection bar and the editing lightbox.
 */
export function StudioPhotosView({ title, album, fixedVisibility, defaultSort = 'taken-desc', notice, empty, heading, heroSub, heroActions, children }: StudioPhotosViewProps) {
  const { access } = useSession();
  const [params, setParams] = useSearchParams();
  const [visibility, setVisibility] = useState<Visibility | undefined>(fixedVisibility);
  const [untagged, setUntagged] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [uploader, setUploader] = useState<string | undefined>();
  const [orient, setOrient] = useState<OrientFilter>('all');
  const [sort, setSort] = useState<StudioSort>(defaultSort);

  const query: StudioQuery = { albumId: album?.id, visibility, untagged, tags, uploader, orient: orient === 'all' ? undefined : orient, sort };
  const queryKey = JSON.stringify(query);
  const photos = useStudioPhotos(query);
  const first = photos.data?.pages[0];
  const grid = usePhotoGridState(photos, queryKey);
  const { items, selected, selectedPhotos, clear, lbIndex, setLbIndex } = grid;

  // "Open in Studio" from Browse: /studio/albums/:id?photo=<id> opens that photo.
  const linked = params.get('photo') ?? undefined;
  const linkedIndex = linked ? items.findIndex((p) => p.id === linked) : -1;
  const linkedDetail = useStudioPhoto(linked && linkedIndex < 0 && first ? linked : undefined);
  useEffect(() => {
    if (linkedIndex >= 0) setLbIndex(linkedIndex);
  }, [linkedIndex, setLbIndex]);
  const closeLinked = () => {
    params.delete('photo');
    setParams(params, { replace: true });
  };

  const total = first?.total ?? 0;
  const vc = first?.visibilityCounts;
  const oc = first?.orientCounts ?? { h: 0, v: 0, sq: 0 };
  const orientCount = (k: OrientFilter) => (k === 'all' ? oc.h + oc.v + oc.sq : oc[k]);
  const counts = useMemo(() => new Map((first?.related ?? []).map((r) => [r.tag, r.count])), [first]);
  const span = fmtRangeIso(first?.firstTakenAt, first?.lastTakenAt);
  const sub = heroSub ?? (first ? [`${fmtCount(total)} photos`, ...(span ? [span] : []), fmtTotal(first.totalBytes)] : []);
  const cover = items.find((p) => p.picked) ?? items[0];
  const visibilities: Visibility[] = access.seesRestricted ? ['unreleased', 'released', 'restricted'] : ['unreleased', 'released'];
  const filtered = !!(visibility !== fixedVisibility || untagged || tags.length || uploader || orient !== 'all');

  const actions = (
    <>
      {heroActions}
      {total > 0 && (
        <button type="button" className="btn sm" onClick={grid.selectAll} disabled={grid.selectingAll}>
          <Icon name="check" />
          Select all
        </button>
      )}
    </>
  );

  return (
    <>
      <Hero
        title={title}
        heading={heading}
        sub={sub}
        coverUrl={cover?.thumbUrl}
        actions={actions}
        layoutKey={[title, ...sub, total > 0, !!heroActions, !!heading].join('|')}
      />
      {children}
      {notice && (
        <div className="notice">
          <Icon name={fixedVisibility ? VISIBILITY_LOOK[fixedVisibility].icon : 'info'} />
          <span>{notice}</span>
        </div>
      )}
      <div className="stoolbar" role="toolbar" aria-label="Filters">
        {!fixedVisibility && (
          <div className="subtabs" role="radiogroup" aria-label="Visibility">
            {[undefined, ...visibilities].map((v) => (
              <button
                type="button"
                key={v ?? 'all'}
                role="radio"
                aria-checked={visibility === v}
                className={visibility === v ? 'on' : ''}
                data-vis={v}
                onClick={() => setVisibility(v)}
              >
                {v ? VISIBILITY_LOOK[v].label : 'All'}
                {vc && <span className="cnt">{fmtCount(v ? vc[v] : vc.unreleased + vc.released + vc.restricted)}</span>}
              </button>
            ))}
          </div>
        )}
        <label className="toggle" title="No event, region, church or group tags yet (a year alone counts as untagged)">
          <input type="checkbox" checked={untagged} onChange={(e) => setUntagged(e.target.checked)} />
          No tags yet{first ? ` · ${fmtCount(first.untagged)}` : ''}
        </label>
        <TagSearch
          id="studioTags"
          active={tags}
          counts={counts}
          onChange={setTags}
          placeholder={album ? 'Filter by tag in this album…' : 'Filter by tag…'}
          scopeLabel="Tags in these photos"
        />
        {(first?.uploaders.length ?? 0) > 1 && (
          <select className="inp sm" aria-label="Uploaded by" value={uploader ?? ''} onChange={(e) => setUploader(e.target.value || undefined)}>
            <option value="">Everyone</option>
            {first!.uploaders.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name} · {u.count}
              </option>
            ))}
          </select>
        )}
        <select className="inp sm" aria-label="Sort" value={sort} onChange={(e) => setSort(e.target.value as StudioSort)}>
          {SORTS.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>
        <div className="seg light" role="radiogroup" aria-label="Photo shape">
          {ORIENT.filter((o) => o.key === 'all' || orientCount(o.key) || orient === o.key).map((o) => (
            <button
              type="button"
              key={o.key}
              role="radio"
              aria-checked={orient === o.key}
              className={orient === o.key ? 'on' : ''}
              title={`${o.label} · ${orientCount(o.key)} photos`}
              onClick={() => setOrient(o.key)}
            >
              {o.icon && <Icon name={o.icon} />}
              <span>{o.short}</span>
            </button>
          ))}
        </div>
      </div>
      {photos.isError ? (
        <div className="empty">
          <h3>Couldn’t load photos</h3>
          {photos.error.message}
        </div>
      ) : !first ? (
        <div className="sentinel">
          <div className="spinner" />
        </div>
      ) : !items.length ? (
        <div className="empty">
          <h3>{filtered ? 'No photos match' : 'All clear'}</h3>
          {filtered ? 'Try another filter.' : empty}
        </div>
      ) : (
        <PhotoGrid
          items={items}
          total={total}
          selected={selected}
          source="glean"
          hasMore={!!photos.hasNextPage}
          loadMore={grid.loadMore}
          onTileClick={grid.onTileClick}
          onSelectDay={grid.onSelectDay}
          grouping={GROUPING[sort]}
        />
      )}
      <SelectionBar photos={selectedPhotos} onClear={clear} share={access.capabilities.share}>
        <StudioActions photos={selectedPhotos} album={album} onDone={clear} />
      </SelectionBar>
      {lbIndex >= 0 && (
        <StudioLightbox
          photos={items}
          total={total}
          index={lbIndex}
          album={album}
          onIndex={setLbIndex}
          onClose={() => {
            setLbIndex(-1);
            if (linked) closeLinked();
          }}
          onNearEnd={grid.loadMore}
        />
      )}
      {lbIndex < 0 && linkedDetail.data && (
        <StudioLightbox photos={[linkedDetail.data]} index={0} album={album} onIndex={() => undefined} onClose={closeLinked} />
      )}
    </>
  );
}
